import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { Toggle, PageSkeleton } from '../ui/primitives';
import { useAdminToast } from './AdminShell';
import { printProductsReport, type ReportMode } from './actions';
import { API_URL } from '../config/api';
import {
  createStockRun,
  openStockPdf,
  printStockSummary,
  stockErrorText,
  useInvalidateStock,
  useStockConfig,
  useStockOverview,
} from '../magazyn/api';

// Kształt z GET /api/printing/report-history (zadania druku, document_type='reports').
type ReportHistoryItem = {
  id: number;
  document_type?: string;
  status?: string;
  error?: string | null;
  created_at: string;
  format?: string | null;
  can_reprint?: boolean;
  has_preview?: boolean;
};

const STATUS_LABEL: Record<string, string> = {
  queued: 'w kolejce',
  picked: 'pobrane',
  printing: 'drukowanie',
  done: 'wydrukowano',
  error: 'błąd',
};

export default function ReportsPage() {
  const push = useAdminToast();
  const qc = useQueryClient();
  // open -> raport „na teraz" (now); new -> delta od ostatniego raportu (since); range -> okno dat.
  const [range, setRange] = useState<'open' | 'new' | 'range'>('open');
  const [preview, setPreview] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery<{ history: ReportHistoryItem[] }>({
    queryKey: ['report-history'],
    queryFn: async () =>
      (await axios.get(`${API_URL}/api/printing/report-history?documentType=reports&limit=20`)).data,
    refetchInterval: 60000,
  });
  const history = data?.history || [];

  const MODE_BY_RANGE: Record<typeof range, ReportMode> = { open: 'now', new: 'since', range: 'range' };

  async function printReport() {
    if (range === 'range' && !dateFrom) {
      push('Podaj datę „od" dla przedziału dat', 'err');
      return;
    }
    setBusy(true);
    try {
      const r = await printProductsReport({
        mode: MODE_BY_RANGE[range],
        from: range === 'range' && dateFrom ? dateFrom : undefined,
        to: range === 'range' && dateTo ? dateTo : undefined,
        preview,
      });
      push(r.message, r.ok ? 'ok' : 'info');
      qc.invalidateQueries({ queryKey: ['report-history'] });
    } catch (e: any) {
      push(e?.response?.data?.message || e?.response?.data?.error || 'Błąd drukowania raportu', 'err');
    } finally {
      setBusy(false);
    }
  }

  async function reprint(id: number) {
    try {
      const r = await axios.post(`${API_URL}/api/printing/jobs/${id}/reprint`);
      push(r.data?.success ? 'Ponowny wydruk w kolejce' : 'Błąd drukowania', r.data?.success ? 'ok' : 'err');
      qc.invalidateQueries({ queryKey: ['report-history'] });
    } catch (e: any) {
      push(e?.response?.data?.message || e?.response?.data?.error || 'Błąd drukowania raportu', 'err');
    }
  }
  async function openPreview(id: number) {
    try {
      const r = await axios.get(`${API_URL}/api/printing/jobs/${id}`);
      const url = r.data?.previewUrl;
      if (url) window.open(url, '_blank', 'noopener');
      else push('Podgląd niedostępny dla tego zadania', 'info');
    } catch (e: any) {
      push(e?.response?.data?.message || e?.response?.data?.error || 'Błąd pobierania podglądu', 'err');
    }
  }

  if (isLoading && !data) return <PageSkeleton table />;

  const opts: ['open' | 'new' | 'range', string, string][] = [
    ['open', 'Na teraz (otwarte zamówienia)', 'Dzisiaj + zaległe + bez terminu — to, co pakować teraz'],
    ['new', 'Nowe od ostatniego raportu', 'Tylko zamówienia, które przyszły po ostatnim wydruku'],
    ['range', 'Przedział dat dostaw', 'Zamówienia z terminem dostawy w wybranym oknie'],
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Raporty</h1>
          <div className="ph-sub">Raport produktów do spakowania · liczony przez UCS, drukowany przez agenta</div>
        </div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div className="card-head"><h3>Nowy raport</h3></div>
          <div className="card-pad">
            {opts.map(([id, lbl, sub]) => (
              <div className="set-row" key={id} style={{ cursor: 'pointer' }} onClick={() => setRange(id)}>
                <Icon name={range === id ? 'check-circle-2' : 'circle'} size={18} style={{ color: range === id ? 'var(--accent)' : 'var(--fg-3)' }} />
                <div className="meta"><div className="t">{lbl}</div><div className="d">{sub}</div></div>
              </div>
            ))}
            {range === 'range' && (
              <div className="wfield-row" style={{ margin: '10px 0' }}>
                <div className="wfield" style={{ marginBottom: 0 }}><label>Od</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
                <div className="wfield" style={{ marginBottom: 0 }}><label>Do (opcjonalnie)</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
              </div>
            )}
            <div className="set-row">
              <div className="meta">
                <div className="t">Wydruk poglądowy</div>
                <div className="d">Znaczy raport „WYDRUK POGLĄDOWY" i nie przesuwa kursora „nowe od…"</div>
              </div>
              <Toggle on={preview} onChange={setPreview} />
            </div>
            <div className="callout" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 12, color: 'var(--fg-2)' }}>
              <Icon name="info" size={15} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>Termin dostawy rozwiązuje UCS według włączonego kontrolera dat. Dla <b>today</b> wszystkie otwarte zamówienia są „na teraz"; dla <b>bird</b> liczą się daty wybrane przez klienta.</span>
            </div>
            <div className="flex" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" disabled={busy} onClick={printReport}>
                <Icon name="file-text" size={16} /> {busy ? 'Generowanie…' : 'Drukuj raport produktów'}
              </button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-head"><h3>Historia raportów</h3><span className="ch-sub">{history.length} pozycji</span></div>
          {history.length === 0 ? (
            <div className="empty" style={{ padding: '34px 16px' }}>
              <div className="ei"><Icon name="file-bar-chart" size={26} /></div>
              <h3>Brak raportów</h3>
              <p>Wygenerowany raport produktów trafia do kolejki druku agenta on-prem i pojawi się tutaj.</p>
            </div>
          ) : (
            <div className="tablewrap"><table className="tbl"><tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>
                    <b>Raport produktów</b>
                    <div className="muted mono" style={{ fontSize: 12 }}>
                      {new Date(h.created_at).toLocaleString('pl-PL')}
                      {h.status ? ` · ${STATUS_LABEL[h.status] || h.status}` : ''}
                    </div>
                  </td>
                  <td><span className="tag-attr">{(h.format || 'PDF').toUpperCase()}</span></td>
                  <td className="right">
                    <div className="flex" style={{ justifyContent: 'flex-end' }}>
                      {h.has_preview && (
                        <button className="btn btn-ghost btn-sm" title="Podgląd" onClick={() => openPreview(h.id)}><Icon name="eye" size={14} /></button>
                      )}
                      {h.can_reprint && (
                        <button className="btn btn-ghost btn-sm" title="Drukuj ponownie" onClick={() => reprint(h.id)}><Icon name="printer" size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      </div>
      <StockReportsCard />
    </div>
  );
}

/* Magazyn (moduł opcjonalny): „co zostało" i lista „Do dobrania" także stąd — obok raportu produktów,
   z którym dziś liczy się chleb ręcznie. Widoczne tylko przy włączonym Magazynie. */
function StockReportsCard() {
  const push = useAdminToast();
  const navigate = useNavigate();
  const invalidate = useInvalidateStock();
  const { data: cfg } = useStockConfig();
  const enabled = !!cfg?.enabled;
  const { data: ov } = useStockOverview({ enabled, refetchInterval: 60000 });
  const [busy, setBusy] = useState<string | null>(null);
  if (!enabled) return null;

  // Nowa lista zastępuje (anuluje) nierozliczoną — jeśli ktoś właśnie przyniósł chleb z piekarni,
  // to, co wziął, nie trafiłoby na stan. Pytamy, zanim ją nadpiszemy.
  const replaceOk = () =>
    !ov?.openRun ||
    window.confirm('Jest nierozliczona lista „Do dobrania”. Nowa lista ją zastąpi — to, co dobrano według starej, nie trafi na stan. Kontynuować?');

  async function run(id: string, fn: () => Promise<string | null>) {
    setBusy(id);
    try {
      const msg = await fn();
      if (msg) push(msg, 'ok');
      invalidate();
    } catch (e: any) {
      push(stockErrorText(e, 'Błąd wydruku magazynu'), 'err');
    } finally {
      setBusy(null);
    }
  }

  const t = ov?.totals;
  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-head">
        <h3>Magazyn</h3>
        <span className="ch-sub">
          {t ? `na stanie ${t.onHand} szt. · potrzeba ${t.demand} szt. · do dobrania ${t.toTake} szt.` : 'stan pieczywa'}
        </span>
        <button className="btn btn-ghost btn-sm right" onClick={() => navigate('/magazyn')}>
          Otwórz Magazyn <Icon name="chevron-right" size={14} />
        </button>
      </div>
      <div className="card-pad">
        <div className="flex" style={{ flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn-primary" disabled={!!busy} onClick={() => run('sum', async () => (await printStockSummary()).message)}>
            <Icon name="printer" size={16} /> {busy === 'sum' ? 'Generowanie…' : 'Drukuj stan magazynu'}
          </button>
          <button
            className="btn btn-ghost"
            disabled={!!busy}
            onClick={() => replaceOk() && run('pick', async () => {
              const r = await createStockRun(true);
              return r.message;
            })}
          >
            <Icon name="clipboard-list" size={16} /> {busy === 'pick' ? 'Generowanie…' : 'Drukuj listę „Do dobrania”'}
          </button>
          <button className="btn btn-ghost" disabled={!!busy} onClick={() => run('sum-pdf', async () => { await openStockPdf({ kind: 'summary' }); return null; })}>
            <Icon name="file-text" size={16} /> Stan — PDF
          </button>
          <button
            className="btn btn-ghost"
            disabled={!!busy}
            onClick={() => replaceOk() && run('pick-pdf', async () => {
              const r = await createStockRun(false);
              if (!r.run) return r.message;
              await openStockPdf({ kind: 'pick', runId: r.run.id });
              return null;
            })}
          >
            <Icon name="file-text" size={16} /> Do dobrania — PDF
          </button>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          Wydruki idą na drukarkę przypisaną do raportów. Nowa lista „Do dobrania” zastępuje nierozliczoną; rozliczasz ją w Magazynie.
        </div>
      </div>
    </div>
  );
}
